import { Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { IoIosArrowDropleft, IoIosArrowDropright } from "react-icons/io";
import { MdKeyboardArrowRight, MdKeyboardArrowLeft } from "react-icons/md";

export default function ScrollableTabs({ tabs = [], activeTab, setActiveTab }) {
  const containerRef = useRef(null);
  const tabRefs = useRef([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  // Update active tab indicator & scroll into view
  useEffect(() => {
    const activeIndex = tabs.findIndex((t) => t.id === activeTab);
    const activeEl = tabRefs.current[activeIndex];

    if (activeEl && containerRef.current) {
      setIndicator({
        left: activeEl.offsetLeft,
        width: activeEl.offsetWidth,
      });

      activeEl.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeTab, tabs]);

  // Check if scroll buttons are needed
  const updateScrollButtons = () => {
    const el = containerRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollWidth > el.clientWidth + el.scrollLeft + 1); // +1 for precision
  };

  useEffect(() => {
    updateScrollButtons();
    const el = containerRef.current;
    if (!el) return;

    const handleScroll = () => updateScrollButtons();
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [tabs]);

  const scroll = (dir) => {
    containerRef.current.scrollBy({
      left: dir === "left" ? -200 : 200,
      behavior: "smooth",
    });
  };

  // Button style function (hover + tooltip)
  const ScrollButton = ({ dir }) => {
    const isLeft = dir === "left";
    const icon = isLeft ? (
      <MdKeyboardArrowLeft size={24} />
    ) : (
      <MdKeyboardArrowRight size={24} />
    );
    return (
      <Tooltip title={isLeft ? "Scroll Left" : "Scroll Right"}>
        <div
          onClick={() => scroll(dir)}
          style={{
            position: "absolute",
            top: "50%",
            [isLeft ? "left" : "right"]: 2,
            transform: "translateY(-50%)",
            zIndex: 20,
            backgroundColor: "white",
            borderRadius: "50%",
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
            e.currentTarget.style.backgroundColor = "#f0f0f0";
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(-50%) scale(1)";
            e.currentTarget.style.backgroundColor = "white";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
          }}
        >
          {icon}
        </div>
      </Tooltip>
    );
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      {showLeft && <ScrollButton dir="left" />}
      <div
        ref={containerRef}
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          position: "relative",
          padding: 4,
          borderRadius: 8,
          backgroundColor: "#e5e7eb",
          flex: 1,
          scrollbarWidth: "none",
        }}
      >
        {/* Active indicator */}
        <span
          style={{
            position: "absolute",
            top: 4,
            bottom: 4,
            left: indicator.left,
            width: indicator.width,
            background: "linear-gradient(to right, #0284c7, #1e40af)",
            borderRadius: 10,
            transition: "all 0.3s ease-in-out",
            zIndex: 5,
          }}
        />
        {tabs.map((tab, i) => {
          const isActive = tab.id === activeTab;
          return (
            <button
            type="button"
              key={tab.id}
              ref={(el) => (tabRefs.current[i] = el)}
              onClick={() => setActiveTab(tab.id)}
              style={{
                position: "relative",
                zIndex: 10,
                padding: "6px 16px",
                whiteSpace: "nowrap",
                borderRadius: 10,
                fontWeight: 600,
                fontSize: 14,
                border: "none",
                cursor: "pointer",
                backgroundColor: "transparent",
                color: isActive ? "white" : "#475569",
                transition: "0.2s ease",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {showRight && <ScrollButton dir="right" />}
    </div>
  );
}
